import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormToggleSection,
} from '@shigo-ui/dashboard-ui';
import { Checkbox, Input, Label } from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import type { PlacementFormValue } from '../../placementFormSchema';

export function PlacementDeliveryFields(props: {
  bidCacheAllowed: boolean;
  stitcherAllowed: boolean;
  a2vAllowed: boolean;
}) {
  'use no memo';
  const { control, register, formState } = useFormContext<PlacementFormValue>();
  const cacheAudioVideo = useWatch({ control, name: 'bcAvEnabled' });
  const cacheVideo = useWatch({ control, name: 'bcVideoEnabled' });
  const dedupeEnabled = useWatch({ control, name: 'stDedupeEnabled' });
  const cacheAvailable = cacheAudioVideo || cacheVideo;
  const bidCacheControls = [
    ...(props.a2vAllowed ? ([['bcAvEnabled', 'placements.bidCacheAudioVideo']] as const) : []),
    ['bcVideoEnabled', 'placements.bidCacheVideo'],
    ['bcAlwaysUseCache', 'placements.alwaysUseCache'],
  ] as const;

  return (
    <DashboardPageFormBody width="lg">
      {props.bidCacheAllowed ? (
        <section className="border-input grid gap-4 rounded-md border p-4">
          <h2 className="text-sm font-semibold">{dashboardT('placements.bidCache')}</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {bidCacheControls.map(([name, label]) => (
              <Controller
                key={name}
                control={control}
                name={name}
                render={({ field }) => (
                  <Label className="flex items-center gap-2 text-sm font-normal">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked === true);
                      }}
                    />
                    {dashboardT(label)}
                  </Label>
                )}
              />
            ))}
          </div>
        </section>
      ) : null}
      {/* Dual mode belongs to the bid cache, not the stitcher, and is always available. */}
      <Controller
        control={control}
        name="dmEnabled"
        render={({ field }) => (
          <DashboardPageFormToggleSection
            label={dashboardT('placements.dualMode')}
            checked={field.value}
            onCheckedChange={field.onChange}
          >
            <div className="grid gap-5 md:grid-cols-2">
              {(['dmShortTimeoutMs', 'dmLongTimeoutMs'] as const).map((name) => (
                <DashboardPageFormField key={name} label={dashboardT(`placements.${name}`)}>
                  <Input type="number" min="0" {...register(name)} />
                  <p className="text-muted-foreground text-xs">
                    {dashboardT('placements.stitcher.timeoutHint')}
                  </p>
                </DashboardPageFormField>
              ))}
            </div>
          </DashboardPageFormToggleSection>
        )}
      />
      {props.stitcherAllowed ? (
        <Controller
          control={control}
          name="stitcherEnabled"
          render={({ field }) => (
            <DashboardPageFormToggleSection
              label={dashboardT('placements.stitcher')}
              checked={field.value}
              onCheckedChange={field.onChange}
            >
              <div className="grid gap-5 md:grid-cols-2">
                {stitcherNumberFields.map(({ name, description, min, max }) => (
                  <DashboardPageFormField
                    key={name}
                    label={dashboardT(`placements.${name}`)}
                    error={formState.errors[name]?.message}
                  >
                    <Input
                      type="number"
                      min={min}
                      max={max}
                      disabled={name === 'stMaxBidsFromCache' && !cacheAvailable}
                      aria-invalid={Boolean(formState.errors[name])}
                      {...register(name)}
                    />
                    <p className="text-muted-foreground text-xs">
                      {dashboardT(
                        name === 'stMaxBidsFromCache' && !cacheAvailable
                          ? 'placements.stitcher.enableCacheHint'
                          : description,
                      )}
                    </p>
                  </DashboardPageFormField>
                ))}
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {(
                  [
                    ['stIgnoreAdDuration', 'placements.ignoreAdDuration'],
                    ['stDedupeEnabled', 'placements.dedupe'],
                  ] as const
                ).map(([name, label]) => (
                  <Controller
                    key={name}
                    control={control}
                    name={name}
                    render={({ field: option }) => (
                      <Label className="flex items-center gap-2 text-sm font-normal">
                        <Checkbox
                          checked={option.value}
                          onCheckedChange={(checked) => {
                            option.onChange(checked === true);
                          }}
                        />
                        {dashboardT(label)}
                      </Label>
                    )}
                  />
                ))}
              </div>
              {dedupeEnabled ? (
                <div className="grid gap-3 border-l pl-4 md:grid-cols-3">
                  {(
                    [
                      ['stDedupeUniqueMediaFile', 'placements.uniqueMedia'],
                      ['stDedupeUniqueAdvertiserDomain', 'placements.uniqueAdvertiser'],
                      ['stDedupeUniqueIABCategory', 'placements.uniqueIab'],
                    ] as const
                  ).map(([name, label]) => (
                    <Controller
                      key={name}
                      control={control}
                      name={name}
                      render={({ field: option }) => (
                        <Label className="flex items-center gap-2 text-sm font-normal">
                          <Checkbox
                            checked={option.value}
                            onCheckedChange={(checked) => {
                              option.onChange(checked === true);
                            }}
                          />
                          {dashboardT(label)}
                        </Label>
                      )}
                    />
                  ))}
                </div>
              ) : null}
            </DashboardPageFormToggleSection>
          )}
        />
      ) : null}
    </DashboardPageFormBody>
  );
}

const stitcherNumberFields: ReadonlyArray<{
  name:
    | 'stAcceptPercentageOfFloor'
    | 'stMaxBids'
    | 'stMaxAvBids'
    | 'stMaxVideoBids'
    | 'stMaxBidsFromCache'
    | 'stPoddingFloor'
    | 'stFeeCpm';
  description: DashboardMessageKey;
  min: number;
  max?: number;
}> = [
  {
    name: 'stAcceptPercentageOfFloor',
    description: 'placements.stitcher.percentageHint',
    min: 0,
    max: 100,
  },
  { name: 'stMaxBids', description: 'placements.stitcher.maxBidsHint', min: 2, max: 5 },
  { name: 'stMaxAvBids', description: 'placements.stitcher.maxAvBidsHint', min: 0, max: 5 },
  {
    name: 'stMaxVideoBids',
    description: 'placements.stitcher.maxVideoBidsHint',
    min: 0,
    max: 5,
  },
  {
    name: 'stMaxBidsFromCache',
    description: 'placements.stitcher.maxCacheBidsHint',
    min: 0,
    max: 5,
  },
  { name: 'stPoddingFloor', description: 'placements.stitcher.nonnegativeHint', min: 0 },
  { name: 'stFeeCpm', description: 'placements.stitcher.nonnegativeHint', min: 0 },
];
